import { api } from '../api';
import type {
  Company,
  CreateCompanyInput,
  UpdateCompanyInput,
} from '@/types/company';
import type { User } from '@/types/user';
import type { Material } from '@/types/material';
import type { Recipe } from '@/types/recipe';
import type { Menu } from '@/types/menu';
import type { PaginationParams, PaginatedResponse } from '@/types/common';

export type CompanyInclude = 'address';

type CompaniesApiResponse = Company[] | PaginatedResponse<Company>;

function isPaginatedCompaniesResponse(payload: unknown): payload is PaginatedResponse<Company> {
  return !!payload && typeof payload === 'object' && Array.isArray((payload as PaginatedResponse<Company>).data);
}

function toCompaniesArray(payload: CompaniesApiResponse): Company[] {
  return isPaginatedCompaniesResponse(payload) ? payload.data : payload;
}

export const companyApi = {
  getAll: async () => {
    const response = await api.get<CompaniesApiResponse>('/companies');
    return toCompaniesArray(response);
  },
  paginate: (params?: PaginationParams & { isActive?: boolean; organizationId?: number; include?: CompanyInclude[] }) => {
    const query = new URLSearchParams();
    if (params) {
      const { include, ...rest } = params;
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== undefined && v !== null) query.set(k, String(v));
      });
      include?.forEach((v) => query.append('include', v));
    }
    const qs = query.toString();
    return api.get<PaginatedResponse<Company>>(`/companies${qs ? `?${qs}` : ''}`);
  },
  getById: (id: number, include?: CompanyInclude[]) => {
    const query = new URLSearchParams();
    include?.forEach((v) => query.append('include', v));
    const qs = query.toString();
    return api.get<Company>(`/companies/${id}${qs ? `?${qs}` : ''}`);
  },
  create: (data: CreateCompanyInput) => api.post<Company>('/companies', data),
  update: (id: number, data: UpdateCompanyInput) => api.patch<Company>(`/companies/${id}`, data),
  delete: (id: number) => api.delete(`/companies/${id}`),

  // Company-scoped sub-resources
  getUsers: (companyId: number) => api.get<User[]>(`/companies/${companyId}/users`),
  getMaterials: (companyId: number) => api.get<Material[]>(`/companies/${companyId}/materials`),
  getRecipes: (companyId: number) => api.get<Recipe[]>(`/companies/${companyId}/recipes`),
  getMenus: (companyId: number) => api.get<Menu[]>(`/companies/${companyId}/menus`),
  getGlobalStats: () => api.get<{ total: number; active: number; inactive: number }>('/companies/stats'),
  getScopedStats: (filters: { organizationId?: number }) => {
    const params = new URLSearchParams();
    if (filters?.organizationId !== undefined) params.set('organizationId', String(filters.organizationId));
    const query = params.toString();
    return api.get<{ total: number; active: number; inactive: number }>(`/companies/stats${query ? `?${query}` : ''}`);
  },
};
